/**
 * SetupTab — the full thirteen-step property workflow.
 *
 * First tab in the property detail modal. Each step row carries its
 * current state, a plain-English description, any active warning, and a
 * primary action button that deep-links to the tab where the work is done.
 *
 * Source of truth is `setup-steps.ts` so the SetupTab and the snapshot
 * strip under the modal header can never disagree.
 */

import { useEffect, useMemo, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { computeSetupSteps, type SetupStep } from './setup-steps';
import {
  POS_GOLD,
  POS_GOLD_DARK,
  POS_GOLD_SOFT_BG,
  POS_GOLD_LIGHT_BG,
  POS_GOLD_BORDER,
  POS_GOLD_TRACK,
} from '../constants';
import type { EnrichedProperty, PropertyTab } from './types';

interface SetupTabProps {
  supabase: SupabaseClient;
  property: EnrichedProperty;
  onJumpToTab: (tab: PropertyTab) => void;
}

const TAB_LABEL: Record<PropertyTab, string> = {
  setup: 'Setup',
  overview: 'Overview',
  property: 'Property',
  records: 'Records',
  rooms: 'Rooms',
};

export default function SetupTab({ supabase, property, onJumpToTab }: SetupTabProps) {
  const [agreementOnFile, setAgreementOnFile] = useState<boolean | null>(null);
  const [hasActiveCertificate, setHasActiveCertificate] = useState<boolean | null>(null);
  const [hasSuiteAccess, setHasSuiteAccess] = useState<boolean | null>(null);
  const [ambassadorDecided, setAmbassadorDecided] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      // Agreement = legacy PDF on the property row OR an auto-confirmed
      // property_agreements row from the invoice T&C path (which is how
      // almost every active property's agreement is recorded today).
      // Suite access = any pending invite OR any granted property /
      // group manager row for this property.
      const groupId = (property as unknown as { group_id?: string | null }).group_id ?? null;
      const [
        propAgreementRes,
        invoiceAgreementRes,
        certRes,
        suiteInviteRes,
        propMgrRes,
        groupMgrRes,
      ] = await Promise.all([
        supabase
          .from('hosp_properties')
          .select('agreement_pdf_url, ambassador_decided')
          .eq('id', property.id)
          .maybeSingle(),
        supabase
          .from('property_agreements')
          .select('id')
          .eq('property_id', property.id)
          .limit(1),
        supabase
          .from('cert_certificates')
          .select('id, certificate_status')
          .eq('property_id', property.id)
          .eq('certificate_status', 'valid')
          .limit(1),
        supabase
          .from('charter_suite_invites')
          .select('id')
          .eq('property_id', property.id)
          .limit(1),
        supabase
          .from('hosp_property_managers')
          .select('id')
          .eq('property_id', property.id)
          .limit(1),
        groupId
          ? supabase
              .from('hosp_group_managers')
              .select('id')
              .eq('group_id', groupId)
              .limit(1)
          : Promise.resolve({ data: [] as Array<{ id: string }> }),
      ]);
      if (cancelled) return;
      const propRow = propAgreementRes.data as { agreement_pdf_url: string | null; ambassador_decided: boolean | null } | null;
      const propAgreement = !!propRow?.agreement_pdf_url;
      const invoiceAgreement = Array.isArray(invoiceAgreementRes.data) && invoiceAgreementRes.data.length > 0;
      setAgreementOnFile(propAgreement || invoiceAgreement);
      setHasActiveCertificate(Array.isArray(certRes.data) && certRes.data.length > 0);
      setAmbassadorDecided(propRow?.ambassador_decided === true);
      const suiteInvite = Array.isArray(suiteInviteRes.data) && suiteInviteRes.data.length > 0;
      const propManager = Array.isArray(propMgrRes.data) && propMgrRes.data.length > 0;
      const groupManager = Array.isArray(groupMgrRes.data) && groupMgrRes.data.length > 0;
      setHasSuiteAccess(suiteInvite || propManager || groupManager);
    }
    load();
    return () => { cancelled = true; };
  }, [supabase, property.id, property]);

  const steps = useMemo<SetupStep[]>(
    () => computeSetupSteps(property, {
      agreementOnFile: agreementOnFile === true,
      hasActiveCertificate: hasActiveCertificate === true,
      hasSuiteAccess: hasSuiteAccess === true,
      ambassadorDecided: ambassadorDecided === true,
    }),
    [property, agreementOnFile, hasActiveCertificate, hasSuiteAccess, ambassadorDecided],
  );

  const required = steps.filter((s) => !s.optional && !s.disabled);
  const requiredDone = required.filter((s) => s.done).length;
  const requiredTotal = required.length;
  const currentKey = required.find((s) => !s.done)?.key ?? null;

  const stillLoading = agreementOnFile === null || hasActiveCertificate === null || hasSuiteAccess === null || ambassadorDecided === null;

  return (
    <div className="flex flex-col gap-lg">

      {/* Header banner */}
      <div style={{
        padding: '0.875rem 1rem',
        background: POS_GOLD_SOFT_BG,
        border: `1px solid ${POS_GOLD_BORDER}`,
        borderRadius: 'var(--radius)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        flexWrap: 'wrap',
      }}>
        <strong style={{ fontSize: 'var(--text-lg)', color: POS_GOLD_DARK }}>
          {requiredDone === requiredTotal ? 'Setup complete' : `${requiredDone} of ${requiredTotal} required steps complete`}
        </strong>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{
            width: '100%',
            height: '6px',
            background: POS_GOLD_TRACK,
            borderRadius: '999px',
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${Math.round((requiredDone / Math.max(1, requiredTotal)) * 100)}%`,
              height: '100%',
              background: POS_GOLD,
              transition: 'width 240ms ease',
            }} />
          </div>
        </div>
      </div>

      {stillLoading && (
        <div style={{ padding: '1rem', fontSize: 'var(--text-base)', color: 'var(--color-text-light)', textAlign: 'center' }}>
          Loading setup status…
        </div>
      )}

      {!stillLoading && (
        <div className="flex flex-col gap-sm">
          {steps.map((step) => {
            const isCurrent = step.key === currentKey;
            return (
              <div
                key={step.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  padding: '0.875rem 1rem',
                  borderRadius: 'var(--radius)',
                  border: isCurrent
                    ? `2px solid ${POS_GOLD}`
                    : step.done
                      ? '1px solid #d1fae5'
                      : '1px solid var(--color-border, #e5e7eb)',
                  background: isCurrent
                    ? POS_GOLD_LIGHT_BG
                    : step.done
                      ? 'var(--color-success-bg)'
                      : step.disabled
                        ? 'var(--color-bg)'
                        : '#ffffff',
                  opacity: step.disabled ? 0.6 : 1,
                }}
              >
                {/* Status icon */}
                <span
                  aria-hidden="true"
                  style={{
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px',
                    borderRadius: '999px',
                    background: step.done ? 'var(--color-success)' : isCurrent ? POS_GOLD : 'transparent',
                    color: step.done || isCurrent ? '#ffffff' : 'var(--color-border)',
                    border: step.done || isCurrent ? 'none' : '1.5px solid #d1d5db',
                    fontSize: 'var(--text-base)',
                    fontWeight: 700,
                    marginTop: '1px',
                  }}
                >
                  {step.done ? '✓' : isCurrent ? '→' : ''}
                </span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    flexWrap: 'wrap',
                    marginBottom: '0.125rem',
                  }}>
                    <span style={{
                      fontSize: 'var(--text-lg)',
                      fontWeight: 600,
                      color: step.done ? 'var(--color-success-text)' : isCurrent ? POS_GOLD_DARK : 'var(--color-text, #111827)',
                    }}>
                      {step.label}
                    </span>
                    {step.optional && (
                      <span style={{
                        fontSize: 'var(--text-xs)',
                        padding: '0.0625rem 0.375rem',
                        borderRadius: '999px',
                        background: 'var(--color-bg, #f3f4f6)',
                        color: 'var(--color-text-light, #6b7280)',
                        fontWeight: 600,
                        letterSpacing: '0.025em',
                        textTransform: 'uppercase',
                      }}>Optional</span>
                    )}
                    {step.driver === 'system' && !step.disabled && (
                      <span style={{
                        fontSize: 'var(--text-xs)',
                        padding: '0.0625rem 0.375rem',
                        borderRadius: '999px',
                        background: 'var(--color-info-bg)',
                        color: 'var(--color-info-text)',
                        fontWeight: 600,
                        letterSpacing: '0.025em',
                        textTransform: 'uppercase',
                      }}>Auto</span>
                    )}
                  </div>
                  <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light, #6b7280)', lineHeight: 1.5 }}>
                    {step.description}
                  </div>
                  {step.warning && (
                    <div style={{
                      marginTop: '0.5rem',
                      fontSize: 'var(--text-sm)',
                      color: '#a16207',
                      background: '#fef9c3',
                      border: '1px solid #fde68a',
                      borderRadius: 'var(--radius-md)',
                      padding: '0.375rem 0.625rem',
                      display: 'inline-block',
                    }}>{step.warning}</div>
                  )}
                </div>

                {/* Action button */}
                {step.tab && !step.disabled && (
                  <button
                    type="button"
                    onClick={() => step.tab && onJumpToTab(step.tab)}
                    style={{
                      flexShrink: 0,
                      padding: '0.375rem 0.75rem',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--color-border, #e5e7eb)',
                      background: '#ffffff',
                      color: isCurrent ? POS_GOLD_DARK : 'var(--color-text, #111827)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      alignSelf: 'center',
                    }}
                  >
                    {step.done ? 'Review →' : `Go to ${TAB_LABEL[step.tab]} →`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
